import React, { useEffect, useId, useRef, useState } from 'react';

import { use2gisMapReady } from 'hooks/use2gisMapReady';
import { NewTaskAssignmentResult } from 'types/interfaces/TaskAssignmentResult';

interface AutoEmployeeAssignmentMapProps {
  data?: NewTaskAssignmentResult[];
  containerId?: string;
  height?: string | number;
}

const EMPTY_DATA: NewTaskAssignmentResult[] = [];

const createEmployeeIcon = () => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="11" fill="#2563EB" stroke="white" stroke-width="2"/>
      <circle cx="12" cy="9" r="3" fill="white"/>
      <path d="M6 18c0-3.5 2.7-6 6-6s6 2.5 6 6" fill="white" opacity="0.8"/>
    </svg>
  `;

  return window.DG.icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(svg),
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

const createTaskIcon = (order: number, color: string = '#DC2626') => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="11" fill="${color}" stroke="white" stroke-width="2"/>
      <text x="12" y="16" text-anchor="middle" fill="white" font-size="12" font-weight="bold">${order}</text>
    </svg>
  `;

  return window.DG.icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(svg),
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28]
  });
};

const AutoEmployeeAssignmentMap: React.FC<AutoEmployeeAssignmentMapProps> = ({
  data = EMPTY_DATA,
  containerId,
  height = '500px'
}) => {
  const mapRef = useRef<any>(null);
  const overlayLayerRef = useRef<any>(null);
  const [isMapCreated, setIsMapCreated] = useState(false);
  const isApiReady = use2gisMapReady();

  /* --- FIX(unique-container-id): начало --- */
  const reactId = useId();
  const mapContainerId =
    containerId ?? `auto-assignment-map-${reactId.replace(/:/g, '')}`;
  /* --- FIX(unique-container-id): конец --- */

  useEffect(() => {
    if (!isApiReady) {
      return;
    }

    if (!document.getElementById(mapContainerId)) {
      console.error(`Container ${mapContainerId} not found`);
      return;
    }

    let map: any;

    try {
      map = window.DG.map(mapContainerId, {
        center: [55.67479, 37.504028],
        zoom: 13,
        key: 'b78aa276-8aa3-4a09-a608-4daeb2bb953c'
      });
    } catch (error) {
      console.error('Ошибка создания карты:', error);
      return;
    }

    mapRef.current = map;
    overlayLayerRef.current = window.DG.layerGroup().addTo(map);
    setIsMapCreated(true);

    /* --- FIX(invalidate-size): начало --- */
    const rafId = window.requestAnimationFrame(() => map.invalidateSize());
    /* --- FIX(invalidate-size): конец --- */

    return () => {
      /* --- FIX(invalidate-size): начало --- */
      window.cancelAnimationFrame(rafId);
      /* --- FIX(invalidate-size): конец --- */
      setIsMapCreated(false);
      overlayLayerRef.current = null;
      mapRef.current = null;
      map.remove();
    };
  }, [isApiReady, mapContainerId]);

  useEffect(() => {
    const map = mapRef.current;
    const overlayLayer = overlayLayerRef.current;

    if (!isMapCreated || !map || !overlayLayer) {
      return;
    }

    overlayLayer.clearLayers();

    const bounds = window.DG.latLngBounds();

    data.forEach((employee) => {
      /* --- FIX(empty-result-guard): начало --- */
      // В ветке ошибки 400 бэкенд отдаёт записи вида { taskId, errors },
      // то есть без tasks и без координат старта. Раньше это не всплывало,
      // потому что карта часто вообще не инициализировалась и эффект не
      // доходил до отрисовки. Теперь инициализация надёжная — пропускаем
      // такие записи, иначе здесь будет TypeError.
      const employeeTasks = employee.tasks ?? [];

      if (
        typeof employee.startLatitude !== 'number' ||
        typeof employee.startLongitude !== 'number'
      ) {
        return;
      }
      /* --- FIX(empty-result-guard): конец --- */

      const employeeMarker = window.DG.marker(
        [employee.startLatitude, employee.startLongitude],
        { icon: createEmployeeIcon() }
      ).bindPopup(`
        <div style="padding: 8px;">
          <strong>Сотрудник:</strong><br/>
          ${employee.displayName}<br/>
          <strong>ID:</strong> ${employee.personId}<br/>
          <strong>Нарядов:</strong> ${employeeTasks.length}
        </div>
      `);
      employeeMarker.addTo(overlayLayer);
      bounds.extend([employee.startLatitude, employee.startLongitude]);

      const sortedTasks = [...employeeTasks].sort((a, b) => a.order - b.order);

      sortedTasks.forEach((task) => {
        const taskMarker = window.DG.marker([task.latitude, task.longitude], {
          icon: createTaskIcon(task.order + 1)
        }).bindPopup(`
          <div style="padding: 8px; max-width: 300px;">
            <strong>Наряд #${task.taskId}</strong><br/>
            <strong>Порядковый номер:</strong> ${task.order + 1}<br/>
            <strong>Тип:</strong> ${task.taskType.label}<br/>
            <strong>Отдел:</strong> ${task.department.label}<br/>
            <strong>Название:</strong> ${task.name || 'Без названия'}<br/>
            <strong>Координаты:</strong><br/>
            Широта: ${task.latitude}°<br/>
            Долгота: ${task.longitude}°
          </div>
        `);
        taskMarker.addTo(overlayLayer);
        bounds.extend([task.latitude, task.longitude]);
      });

      if (sortedTasks.length > 0) {
        const firstTask = sortedTasks[0];
        const line1 = window.DG.polyline(
          [
            [employee.startLatitude, employee.startLongitude],
            [firstTask.latitude, firstTask.longitude]
          ],
          {
            color: '#000205',
            weight: 2,
            dashArray: '5 5',
            opacity: 0.7
          }
        );
        line1.addTo(overlayLayer);
        for (let i = 0; i < sortedTasks.length - 1; i++) {
          const current = sortedTasks[i];
          const next = sortedTasks[i + 1];
          const line = window.DG.polyline(
            [
              [current.latitude, current.longitude],
              [next.latitude, next.longitude]
            ],
            {
              color: '#000205',
              weight: 2,
              dashArray: '5 5',
              opacity: 0.5
            }
          );
          line.addTo(overlayLayer);
        }
      }
    });

    /* --- FIX(empty-result-guard): начало --- */
    // bounds.isValid() вместо data.length > 0: если все записи отфильтрованы
    // гардом выше, fitBounds с пустыми границами бросает исключение.
    const canFitBounds = bounds.isValid();
    /* --- FIX(empty-result-guard): конец --- */

    if (canFitBounds) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [data, isMapCreated]);

  return (
    <div style={{ width: '100%', height: height, position: 'relative' }}>
      <div id={mapContainerId} style={{ width: '100%', height: '100%' }}></div>
    </div>
  );
};

export default AutoEmployeeAssignmentMap;
